// CACHE BUST v4.0 - Contemporary design with glass morphism
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver.jsx';
function Services() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [headerRef, headerVisible] = useIntersectionObserver({
    threshold: 0.2
  });
  const [showStickyAnchor, setShowStickyAnchor] = useState(false);

  // Detect when Services section is in viewport
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const servicesSection = document.getElementById('services');
    if (!servicesSection) return;
    const observer = new IntersectionObserver(([entry]) => {
      // Show sticky anchor when services section is in view and user has scrolled past header
      setShowStickyAnchor(entry.isIntersecting && window.scrollY > 100);
    }, {
      threshold: 0.1,
      rootMargin: '-100px 0px 0px 0px' // Offset from top
    });
    observer.observe(servicesSection);
    const handleScroll = () => {
      const rect = servicesSection.getBoundingClientRect();
      const isInView = rect.top < window.innerHeight && rect.bottom > 0;
      setShowStickyAnchor(isInView && window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const services = [{
    id: 1,
    title: 'Individual Therapy',
    image: 'https://maker-devi-dev.s3-accelerate.amazonaws.com/uploads/user_33tbl7B6e1bXN6KW4wjrevvqrth/cmhd1gtfc0001vb0yeeofdn9z/messages/asian-powerful-free-lady-rxunbv15g1.jpg',
    description: 'Trauma-informed therapy and coaching to heal people-pleasing at its roots. In-person in Redwood City and online throughout California. Regulate your nervous system, reclaim your voice, and break generational cycles.',
    details: ['Nervous system regulation & felt safety', 'Set boundaries without shame or chronic guilt', 'End generational trauma in your lifetime', 'In-person in Redwood City & Online across California'],
    cta: 'Learn More',
    color: 'sage',
    link: '/services/individual-sessions',
    featured: false
  }, {
    id: 2,
    title: 'Group Coaching',
    image: 'https://maker-devi-dev.s3-accelerate.amazonaws.com/uploads/user_33tbl7B6e1bXN6KW4wjrevvqrth/cmhd1gtfc0001vb0yeeofdn9z/images/unsplash-image-roy7lf1zxl8-69digfzkz9w.jpg',
    description: 'The Compassionate Connect Method (CCM™) — regulate your nervous system in a group experiential with other highly motivated women in a safe, trauma-informed space.',
    details: ['Practice nervous system regulation and coregulation', 'Practice listening to and honoring your body in safe ways', 'Practice releasing worry and pleasing parts', 'Closed and confidential, experiential group'],
    cta: 'Learn More',
    color: 'warm',
    link: '/services/group-coaching',
    badge: 'Group starts March, 2026'
  }
  // Other offerings temporarily hidden to be brought back later:
  // - Clinical Consultations (id: 5)
  // - Mom's Empowerment Circle (id: 3)
  // - South Asian Sisters (id: 6)
  // - People-Pleasing in the Therapy Room Free Guide (id: 4)
  ];
  return <section id="services" className="relative py-20 md:py-16 mt-0 mb-0 ml-0 mr-0 pl-0 pr-0">
      {/* Sticky Section Anchor - Visible when in viewport */}
      <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-40 transition-all duration-500 ${showStickyAnchor ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}>
        <div className="px-5 py-2.5 glass-sage rounded-full shadow-lg border border-sage-200/50 backdrop-blur-md">
          <span className="text-sage-700 text-sm font-semibold tracking-wide flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            Services
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-800 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">How I Can Help</h2>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
            Whether you're looking for focused one-on-one therapy or deep experiential group practice, I offer compassionate support tailored to your journey.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
          {services.map((service, index) => <Link key={service.id} to={service.link} className={`group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 hover:scale-[1.02] cursor-pointer block ${service.featured ? 'md:col-span-2 lg:col-span-1' : ''} ${service.wideCard ? 'md:col-span-2' : ''}`} style={{
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
        }} onMouseEnter={() => setHoveredCard(service.id)} onMouseLeave={() => setHoveredCard(null)} data-tracking-id={`link-7d9f2db1b376fb29_${service.id}`}>
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" style={service.id === 3 ? {
              objectPosition: 'center 20%'
            } : {}} />
                <div className={`absolute inset-0 bg-gradient-to-t from-${service.color}-900/90 via-${service.color}-800/60 to-transparent`}></div>
                
                {/* Badge with Enhanced Glass Effect */}
                {service.badge && <div className="absolute top-4 right-4 animate-float">
                    <span className="inline-block px-3 py-1 glass-warm rounded-full text-white text-xs font-semibold uppercase tracking-wide glow-soft">
                      {service.badge}
                    </span>
                  </div>}
                
                {/* Title Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl md:text-3xl font-serif font-semibold text-white mb-2">
                    {service.title}
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                {/* Micro-tagline for service 5 */}
                {service.microTagline && <p className="text-sage-600 italic text-sm mb-3">{service.microTagline}</p>}
                
                <p className="text-gray-600 leading-relaxed mb-6" dangerouslySetInnerHTML={{
              __html: service.description
            }}>
                </p>

                {/* Details List */}
                <ul className="space-y-3 mb-8">
                  {service.details.map((detail, index) => <li key={index} className="flex items-start gap-3">
                      <svg className={`w-5 h-5 text-${service.color}-500 flex-shrink-0 mt-0.5`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 text-sm">{detail}</span>
                    </li>)}
                </ul>

                {/* CTA Button with Enhanced Interaction */}
                <div className={`block w-full py-3 px-6 rounded-full font-semibold transition-all duration-300 text-center btn-ripple hover:scale-105 transform ${service.color === 'sage' ? 'bg-sage-600 hover:bg-sage-700 text-white hover:shadow-sage' : service.color === 'warm' ? 'bg-warm-500 hover:bg-warm-600 text-white glow-warm' : service.color === 'softblue' ? 'bg-softblue-500 hover:bg-softblue-600 text-white' : 'bg-cream-600 hover:bg-cream-700 text-white'} hover:shadow-xl`}>
                  {service.cta}
                </div>
              </div>
            </Link>)}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6 text-lg">
            Ready to start? Book a free consultation to discuss which path is right for you.
          </p>
          <a className="inline-block px-8 py-4 bg-warm-500 text-white rounded-full text-base font-semibold hover:bg-warm-600 transition-all duration-300 hover:shadow-xl transform hover:scale-105" data-tracking-id="link-eaa98174f6af7bb9" href="https://calendly.com/yphilipos/30min">
            Schedule Your Free Call
          </a>
        </div>
      </div>
    </section>;
}
export default Services;